import React, { forwardRef, useEffect, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { ControlledInput } from '../../ui/Input/Input';
import Button, { ButtonToneProps } from '../../ui/Button/Button';
import { useConnectorMutations, useConnectorTemplateQuery } from '../../../services/connector';
import Divider from '../../ui/Divider/Divider';
import { ControlledSelect, Option } from '../../ui/Select/Select';
import { MOCK_API_VERSIONS } from './apiVersionMockData';

const createConnectorSchema = z.record(z.string().min(1, 'Field is required'));

export type CreateConnectorFormValues = z.infer<typeof createConnectorSchema>;

type Props = {
  connectorTemplateId: number;
  allowEdition?: boolean;
  onClose?: () => void;
};

function buildConnectorSchema(fields: { name: string; required: boolean }[], t: (key: string) => string) {
  const shape: Record<string, z.ZodTypeAny> = {};

  fields.forEach(field => {
    shape[field.name] = z.preprocess(
      val => (val == null ? '' : val),
      field.required ? z.string().min(1, t('errors.field_required_error')) : z.string().optional(),
    );
  });

  return z.object(shape);
}

export const IntegrationAddAndActivateForm = forwardRef<HTMLFormElement, Props>(
  ({ connectorTemplateId, allowEdition, onClose }, ref) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { data, isLoading, error } = useConnectorTemplateQuery(connectorTemplateId);

    const filteredFields = useMemo(() => {
      return data?.specific_fields || [];
    }, [data]);

    const connectorSchema = useMemo(() => {
      return buildConnectorSchema(filteredFields, t);
    }, [filteredFields, t]);

    const {
      register,
      control,
      getValues,
      trigger,
      formState: { isValid, isSubmitting },
    } = useForm<z.infer<typeof connectorSchema>>({
      resolver: zodResolver(connectorSchema),
      defaultValues: Object.fromEntries(filteredFields.map((f: any) => [f.name, f.value ?? ''])),
      mode: 'onChange',
      reValidateMode: 'onChange',
    });

    useEffect(() => {
      if (filteredFields.length) {
        trigger();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [connectorTemplateId, trigger]);

    const { createMutation } = useConnectorMutations();

    const onAddAndActivateConnector = async () => {
      try {
        const formValues = getValues();
        const specificFields = Object.entries(formValues).map(([key, value]) => ({
          name: key,
          value,
        }));

        await createMutation.mutateAsync(
          {
            body: {
              specific_fields: specificFields,
              connector_tmpl_id: connectorTemplateId,
              active: true,
            },
          },
          {
            onSuccess: data => {
              toast.success(t('success.connector_created'));
              navigate(`/integrations/connectors/${data.id}`);
              onClose?.();
            },
          },
        );
      } catch (error) {
        toast.error(t('errors.connector_create_failed'));
      }
    };

    const closeOverlay = () => {
      onClose?.();
    };

    if (isLoading) return <div>{t('general.loading')}</div>;
    if (error) return <div>{t('errors.failed_to_load_template')}</div>;

    return (
      <form ref={ref} className="flex flex-col flex-1 gap-16 p-24">
        <div className="flex flex-col gap-[12px]">
          <div className="flex md:flex-row gap-16 md:items-center items-start mb-12 justify-between flex-col">
            <h2 className="text-full typo-title-36">{t('integrations.create_new_connector')}</h2>
          </div>

          <Divider className="my-24" />

          <div className={classNames('grid grid-cols-2 gap-16 w-full')}>
            <div className="text-full typo-text-16--bold mb-20">{t('integrations.connector_credentials')}</div>

            {filteredFields.map((field: any) => {
              return (
                <div key={field.name} className="col-span-2">
                  {field.name === 'api_version' ? (
                    <Controller
                      name={field.name}
                      defaultValue={MOCK_API_VERSIONS[0].value}
                      control={control}
                      render={({ field }) => (
                        <ControlledSelect
                          {...field}
                          control={control}
                          options={MOCK_API_VERSIONS as unknown as Option[]}
                          containerClassName="col-span-2"
                          placeholder={field.name}
                          disabled={!allowEdition}
                          isMultiSelect={false}
                        />
                      )}
                    />
                  ) : (
                    <ControlledInput
                      {...register(field.name)}
                      control={control}
                      label={field.title}
                      placeholder=""
                      disabled={!allowEdition}
                      className="col-span-2"
                      type={field.type === 'password' ? 'password' : 'text'}
                    />
                  )}
                </div>
              );
            })}
            <Button
              className="mr-12"
              onClick={closeOverlay}
              tone={ButtonToneProps.SECONDARY}
              title={t('general.cancel')}
            />
            <Button
              onClick={onAddAndActivateConnector}
              tone={ButtonToneProps.PRIMARY_BLUE}
              title={t('general.confirm')}
              disabled={!isValid || isSubmitting}
            />
          </div>
        </div>
      </form>
    );
  },
);
